import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TextEditorComponent, TextEditorStyle } from '../../../../../shared/components/text-editor';

export interface CouponInstructionStep {
  stepNumber: number;
  title: string;
  description: string;
}

export interface CouponsPanelData {
  title?: string;
  subtitle?: string;
  titleStyle?: TextEditorStyle;
  promoCodeLabel?: string;
  promoCode?: string;
  availabilityLabel?: string;
  washPackages?: string[];
  instructionHeading?: string;
  instructionSteps?: CouponInstructionStep[];
  /** @deprecated Legacy image-based coupon; no longer edited in panel. */
  couponImage?: {
    src?: string;
    alt?: string;
    width?: string;
    height?: string;
    scaletype?: string;
  };
  /** @deprecated Legacy color field kept for backward compatibility. */
  couponColor?: string;
  /** @deprecated Use instructionSteps instead. */
  instructionParagraph?: string;
}

@Component({
  selector: 'app-coupons-panel',
  standalone: true,
  imports: [CommonModule, FormsModule, TextEditorComponent],
  templateUrl: './coupons-panel.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './coupons-panel.component.scss',
})
export class CouponsPanelComponent implements OnInit, OnChanges {
  @Input() data: CouponsPanelData = {
    title: '',
    subtitle: '',
    promoCodeLabel: 'PROMO CODE',
    promoCode: '',
    availabilityLabel: 'Available for the following washes',
    washPackages: [],
    instructionHeading: '',
    instructionSteps: [],
  };
  @Output() close = new EventEmitter<void>();
  @Output() save = new EventEmitter<CouponsPanelData>();
  @Output() dataChange = new EventEmitter<CouponsPanelData>();

  activeTab: 'content' | 'layout' = 'content';
  expandedSections: Record<string, boolean> = {
    heading: true,
    coupon: false,
    instruction: false,
  };

  ngOnInit(): void {
    this.ensureDefaults();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      this.ensureDefaults();
    }
  }

  private ensureDefaults(): void {
    if (!this.data.promoCodeLabel) {
      this.data.promoCodeLabel = 'PROMO CODE';
    }
    if (!this.data.availabilityLabel) {
      this.data.availabilityLabel = 'Available for the following washes';
    }
    if (!this.data.washPackages) {
      this.data.washPackages = [];
    }
    if (!this.data.instructionSteps || this.data.instructionSteps.length === 0) {
      this.data.instructionSteps = this.defaultInstructionSteps();
    }
  }

  private defaultInstructionSteps(): CouponInstructionStep[] {
    return [
      { stepNumber: 1, title: '', description: '' },
      { stepNumber: 2, title: '', description: '' },
      { stepNumber: 3, title: '', description: '' },
    ];
  }

  onClose(): void {
    this.close.emit();
  }

  switchTab(tab: 'content' | 'layout'): void {
    this.activeTab = tab;
  }

  toggleSection(section: string): void {
    const wasExpanded = this.expandedSections[section] || false;

    if (wasExpanded) {
      this.expandedSections[section] = false;
    } else {
      if (section === 'heading' || section === 'coupon' || section === 'instruction') {
        Object.keys(this.expandedSections).forEach(key => {
          if (key === 'heading' || key === 'coupon' || key === 'instruction') {
            this.expandedSections[key] = false;
          }
        });
      }
      this.expandedSections[section] = true;
    }
  }

  isSectionExpanded(section: string): boolean {
    return this.expandedSections[section] || false;
  }

  onTitleChange(value: string): void {
    this.data.title = value;
    this.emitDataChange();
  }

  onTitleStyleChange(style: TextEditorStyle): void {
    this.data.titleStyle = style;
    this.emitDataChange();
  }

  onSubtitleChange(value: string): void {
    this.data.subtitle = value;
    this.emitDataChange();
  }

  onCouponFieldChange(): void {
    this.emitDataChange();
  }

  onInstructionHeadingChange(): void {
    this.emitDataChange();
  }

  onInstructionStepChange(): void {
    this.emitDataChange();
  }

  onWashPackageChange(index: number, value: string): void {
    if (!this.data.washPackages) {
      this.data.washPackages = [];
    }
    this.data.washPackages[index] = value;
    this.emitDataChange();
  }

  addWashPackage(): void {
    if (!this.data.washPackages) {
      this.data.washPackages = [];
    }
    this.data.washPackages.push('');
    this.emitDataChange();
  }

  removeWashPackage(index: number): void {
    if (!this.data.washPackages) {
      return;
    }
    this.data.washPackages.splice(index, 1);
    this.emitDataChange();
  }

  addInstructionStep(): void {
    if (!this.data.instructionSteps) {
      this.data.instructionSteps = [];
    }
    this.data.instructionSteps.push({
      stepNumber: this.data.instructionSteps.length + 1,
      title: '',
      description: '',
    });
    this.reindexInstructionSteps();
    this.emitDataChange();
  }

  removeInstructionStep(index: number): void {
    if (!this.data.instructionSteps || this.data.instructionSteps.length <= 1) {
      return;
    }
    this.data.instructionSteps.splice(index, 1);
    this.reindexInstructionSteps();
    this.emitDataChange();
  }

  private reindexInstructionSteps(): void {
    this.data.instructionSteps?.forEach((step, index) => {
      step.stepNumber = index + 1;
    });
  }

  trackByIndex(index: number): number {
    return index;
  }

  private emitDataChange(): void {
    this.dataChange.emit({ ...this.data });
  }

  onSave(): void {
    this.save.emit({ ...this.data });
  }
}
